/**
 * Pure listing formatting helpers, ported from the legacy
 * `app/shared/utils/listingUtils` module and `numberUtils.compact`.
 *
 * Kept minimal — only the symbols the HALO modules actually need.
 */

type Separator = '-' | 'to' | undefined;
type DisplayType = 'compact' | 'long' | undefined;

export const outOfRange = (min: number, max: number): boolean => {
  if (min === Infinity || !(min >= 0)) return true;
  if (max === Infinity || !(max >= 0)) return true;
  return false;
};

/**
 * Compact number formatter — mirrors the legacy `numberUtils.compact` shape,
 * e.g. 1234 → "1,234", 1500000 → "1.5M". Keeps commas for 4-digit numbers
 * (typical rent prices) and uses a suffix for millions.
 */
export const compactNumber = (raw: number | string | undefined | null): string => {
  if (raw == null) return '';
  const num = typeof raw === 'string' ? Number(raw.replace(/,/g, '')) : Number(raw);
  if (!Number.isFinite(num)) return '';

  if (Math.abs(num) >= 1_000_000) {
    const v = num / 1_000_000;
    const trimmed = v.toFixed(1).replace(/\.0$/, '');
    return `${trimmed}M`;
  }
  return num.toLocaleString('en-US');
};

export const summaryPriceText = (priceObj: {
  priceLow?: number | null;
  priceHigh?: number | null;
}): string => {
  if (!priceObj.priceLow && !priceObj.priceHigh) {
    return 'Call';
  }

  const raw = [priceObj.priceLow, priceObj.priceHigh].filter((p): p is number => Boolean(p));
  const prices = Array.from(new Set(raw));

  if (prices.length === 1) {
    return `$${compactNumber(prices[0])}`;
  }
  return `$${compactNumber(prices[0])} - $${compactNumber(prices[1])}`;
};

export const truncateListingUpdated = (timeElapsed: string | null | undefined): string => {
  if (!timeElapsed) return '';
  const [firstPart] = timeElapsed.split(',');
  return timeElapsed.includes(',') ? `${firstPart} ago` : firstPart;
};

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

type AvailabilityListing = {
  active?: boolean;
  isUnit?: boolean;
  floorplans?: Array<{ availabilityDate?: string }>;
};

/**
 * "Available {Month} {Day}" from a listing's first floorplan, or "No longer
 * available" for inactive unit listings. Shared by `TrustSignal` (prefixes the
 * "Updated" trust text at the top of the HDP) — availability is independent
 * of listing history/update timestamps and does not represent multi-unit BDPs.
 */
export const getListingAvailabilityDisplay = (
  listing: AvailabilityListing | null | undefined,
): { availabilityDisplay: string; showAvailability: boolean } => {
  if (listing?.isUnit !== true) {
    return { availabilityDisplay: '', showAvailability: false };
  }

  if (listing?.active === false) {
    return { availabilityDisplay: 'No longer available', showAvailability: true };
  }

  const date = listing?.floorplans?.[0]?.availabilityDate;
  if (!date) return { availabilityDisplay: '', showAvailability: false };

  const [, monthStr, dayStr] = date.split('-');
  const month = parseInt(monthStr, 10);
  const day = parseInt(dayStr, 10);
  if (!Number.isFinite(month) || !Number.isFinite(day) || month < 1 || month > 12) {
    return { availabilityDisplay: '', showAvailability: false };
  }

  return { availabilityDisplay: `Available ${MONTH_NAMES[month - 1]} ${day}`, showAvailability: true };
};

export const bedDisplay = (
  minIn: number | undefined,
  maxIn: number | undefined,
  type?: DisplayType,
  customSeparator?: Separator,
): string => {
  let min = Number(minIn);
  let max = Number(maxIn);
  const separator = customSeparator ?? (type === 'compact' ? '-' : 'to');

  if (outOfRange(min, max)) return '';

  if (min === 0) {
    if (max === 0) return 'Studio';
    return type === 'compact' ? `Studio${separator}${max} bd` : `Studio ${separator} ${max} beds`;
  }

  if (min > max) {
    const tmp = min;
    min = max;
    max = tmp;
  }

  if (min !== max) {
    return type === 'compact' ? `${min}${separator}${max} bd` : `${min} ${separator} ${max} beds`;
  }
  if (min === 1) return type === 'compact' ? `${min} bd` : `${min} bed`;
  return type === 'compact' ? `${min} bd` : `${min} beds`;
};

export const bathDisplay = (
  minIn: number | undefined,
  maxIn: number | undefined,
  type?: DisplayType,
  customSeparator?: Separator,
): string => {
  const min = Number(minIn);
  const max = Number(maxIn);
  const separator = customSeparator ?? (type === 'compact' ? '-' : 'to');

  if (outOfRange(min, max)) return '';
  if (min === 0 && max === 0) return '';

  if (min !== max) {
    return type === 'compact' ? `${min}${separator}${max} ba` : `${min} ${separator} ${max} baths`;
  }
  if (min === 1) return type === 'compact' ? `${min} ba` : `${min} bath`;
  return type === 'compact' ? `${min} ba` : `${min} baths`;
};
